// Spreadsheet-style editing of budget lines
import { markupForOut, numOf } from '../core/budget.js';
import { useEffect, useMemo, useRef, useState } from '../core/react.js';
import { COLS } from './budget-cols.js';
import { supplierFor } from '../core/supplier-links.js';

export function useTableEditor({
  sections,
  setSections,
  tab,
  fx,
  supplierDirectory = []   // a supplier typed on a line is linked to the database entry of the same name
}) {
  // The cell being edited is tracked by section and line id, not by position, so a colleague's
  // change arriving mid-edit (lines added or removed above) can't redirect the edit to another line.
  const [edit, setEdit] = useState(null);
  const editing = useMemo(() => {
    if (!edit) return null;
    const si = sections.findIndex(s => s.id === edit.secId);
    const ii = si < 0 ? -1 : (sections[si].items || []).findIndex(it => it.id === edit.itemId);
    return ii < 0 ? null : {
      si,
      ii,
      k: edit.k
    };
  }, [edit, sections]);
  const setEditing = v => setEdit(v ? {
    secId: sections[v.si]?.id,
    itemId: sections[v.si]?.items[v.ii]?.id,
    k: v.k
  } : null);
  const patchItem = (patch) => setSections(prev => prev.map(sec => sec.id !== edit.secId ? sec : {
    ...sec,
    items: sec.items.map(it => it.id !== edit.itemId ? it : {
      ...it,
      ...patch
    })
  }));
  const [editVal, setEditVal] = useState('');
  const inputRef = useRef(null);
  useEffect(() => {
    if (editing && inputRef.current) {
      if (typeof inputRef.current.select === 'function' && inputRef.current.tagName === 'INPUT') {
        inputRef.current.select();
      } else {
        inputRef.current.focus();
      }
    }
  }, [editing]);
  function startEdit(si, ii, k, current, editMode) {
    if (!editMode) return;
    const col = COLS[tab].find(c => c.k === k);
    if (!col?.edit) return;
    let display = current != null ? String(current) : '';
    if (col.type === 'pct') display = current != null ? String(Math.round(current * 10000) / 100) : '0';
    if (k === 'outUSD') display = current != null && isFinite(current) ? String(Math.round(current * 100) / 100) : '';
    setEditing({
      si,
      ii,
      k
    });
    setEditVal(display);
  }
  function commitEdit() {
    if (!editing) return;
    const { k } = editing;
    const col = COLS[tab].find(c => c.k === k);
    let val;
    const NUM_KEYS = ['qty', 'up', 'mu', 'ag', 'ab', 'vat'];
    if (col?.type === 'pct') {
      val = editVal === '' ? 0 : (numOf(editVal) || 0) / 100;
    } else if (NUM_KEYS.includes(k)) {
      val = editVal === '' ? (k === 'ag' || k === 'ab' ? null : 0) : numOf(editVal) || 0;
    } else {
      val = editVal;
    }
    if (k === 'outUSD') {
      // an out-cost typed in sets the markup; the out-cost itself is always worked out from it
      const it = sections.find(s => s.id === edit.secId)?.items.find(i => i.id === edit.itemId);
      const mu = it && editVal.trim() !== '' ? markupForOut(it, fx, numOf(editVal)) : null;
      if (mu != null) patchItem({ mu });
      setEditing(null);
      return;
    }
    if (k === 'sup') {
      const d = supplierFor(val, supplierDirectory);
      patchItem({ sup: d ? d.name : val, supId: d ? d.id : undefined });
    } else patchItem({
      [k]: val
    });
    setEditing(null);
  }
  function commitSelectEdit(newVal) {
    if (!editing) return;
    patchItem({
      [editing.k]: newVal
    });
    setEditing(null);
  }
  function commitSecName(si, newName) {
    setSections(prev => prev.map((sec, s2) => s2 !== si ? sec : {
      ...sec,
      name: newName
    }));
  }
  function addSection() {
    setSections(prev => [...prev, {
      id: 'sec_new_' + Date.now(),
      name: 'New Section',
      items: []
    }]);
  }
  function deleteSection(si) {
    if (!window.confirm('Delete this section and all its lines?')) return;
    setSections(prev => prev.filter((_, i) => i !== si));
  }
  function addItem(si) {
    const sec = sections[si];
    const maxLn = sec.items.reduce((m, it) => Math.max(m, it.ln || 0), 0);
    const newItem = {
      id: 'new_' + Date.now(),
      ln: maxLn + 1,
      desc: 'New item',
      st: 'TBC',
      sup: '',
      ccy: 'USD',
      qty: 1,
      unit: '',
      up: 0,
      vat: 0,
      mu: 0,
      ag: null,
      ab: null,
      note: ''
    };
    setSections(prev => prev.map((sec2, s2) => s2 !== si ? sec2 : {
      ...sec2,
      items: [...sec2.items, newItem]
    }));
  }
  function deleteItem(si, ii) {
    const item = sections[si]?.items[ii];
    if (!item) return;
    if (!window.confirm(`Delete "${item.desc || 'this line'}"?`)) return;
    setSections(prev => prev.map((sec2, s2) => s2 !== si ? sec2 : {
      ...sec2,
      items: sec2.items.filter((_, i2) => i2 !== ii)
    }));
  }
  function cancelEdit() {
    setEditing(null);
  }
  return {
    editing,
    editVal,
    setEditVal,
    inputRef,
    startEdit,
    commitEdit,
    commitSelectEdit,
    cancelEdit,
    commitSecName,
    addSection,
    deleteSection,
    addItem,
    deleteItem
  };
}
